import { useSyncExternalStore } from "react";

import { DEFAULT_PAGE_SIZE, normalizePageSize } from "@/lib/pagedList";

const PAGE_SIZE_KEY = "nexus:list-page-size";
const listeners = new Set<() => void>();
let preferredPageSize: number | null | undefined;

function readPageSize(): number | null {
  if (preferredPageSize !== undefined) return preferredPageSize;
  try {
    const stored = window.localStorage.getItem(PAGE_SIZE_KEY);
    preferredPageSize = stored === null ? null : normalizePageSize(stored);
  } catch {
    preferredPageSize = null;
  }
  return preferredPageSize;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PAGE_SIZE_KEY && event.key !== null) return;
    preferredPageSize = undefined;
    for (const notify of listeners) notify();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** One browser-local row-count preference, shared across lists and tabs. */
export function useListPageSize(fallback = DEFAULT_PAGE_SIZE) {
  const preferred = useSyncExternalStore(subscribe, readPageSize, () => null);
  const setPageSize = (value: number) => {
    preferredPageSize = normalizePageSize(value);
    try {
      window.localStorage.setItem(PAGE_SIZE_KEY, String(preferredPageSize));
    } catch {}
    for (const notify of listeners) notify();
  };
  return [preferred ?? fallback, setPageSize] as const;
}
